import { Accordion, Alert, Button, Card, Spinner } from "flowbite-react";
import Input from "../inputs/Input";
import PhoneInput from "../inputs/PhoneInput";
import CPFInput from "../inputs/CPFInput";
import CNPJInput from "../inputs/CNPJInput";
import CurrencyInput from "../inputs/CurrencyInput";
import SelectInput from "../inputs/SelectInput";
import { Controller, useForm } from "react-hook-form";
import { useEffect, useState, useMemo, useRef } from "react";
import { useQuery } from "react-query";
import axios from "../../config/axiosInstance";
import { ADMIN, CATEGORIES, USERS } from "../../config/apiConfig";
import useCEP from "../../hooks/useCEP";
import { formatToReal, isValidCNPJ, isValidCPF } from "../../utils/utils";
import { FaCheckCircle, FaRegQuestionCircle } from "react-icons/fa";
import moment from "moment";
import { useApplication } from "../../contexts/ApplicationContext";
import { Switch } from "@mui/material";
import { Tooltip } from "react-tooltip";
import { useNavigate } from "react-router-dom";

const mergeHandlers =
  (...fns) =>
  (e) =>
    fns.filter(Boolean).forEach((fn) => fn(e));

// força string com 2 casas decimais (ou vazio)
const toFixedStr = (n) =>
  n == null || n === "" || isNaN(Number(n)) ? "" : Number(n).toFixed(2);

const isRep = (user) =>
  String(user?.Company?.companyTypeId || "")
    .toUpperCase()
    .includes("REPRESENT");

const getBasePrice = (query, tableQueries) =>
  tableQueries?.find((tq) => tq.QueryId === query.id)?.price ?? query.price;

const StepHeader = ({ activeStep }) => (
  <ol className="flex items-center w-full text-sm font-medium text-gray-500 sm:text-base">
    {["Revendedor", "Usuários", "Consultas", "Confirmação"].map(
      (label, idx) => {
        const step = idx + 1;
        const active = activeStep >= step;
        return (
          <li
            key={label}
            className="flex md:w-full items-center sm:after:content-[''] after:w-full after:h-1 after:border-b after:border-gray-200 after:hidden sm:after:inline-block after:mx-6 xl:after:mx-10"
          >
            <span
              className={`flex items-center ${active ? "text-primary" : ""}`}
            >
              <span
                className="inline-flex items-center justify-center w-6 h-6 rounded-full border mr-2"
                style={{
                  borderColor: "var(--primary)",
                  color: active ? "var(--primary)" : "#6b7280",
                }}
              >
                {active ? "✓" : step}
              </span>
              <span className="hidden md:block">{label}</span>
            </span>
          </li>
        );
      }
    )}
  </ol>
);

const SectionCard = ({ title, subtitle, children, right }) => (
  <div className="rounded-2xl border bg-white shadow-sm p-4">
    <div className="flex items-start justify-between gap-4 mb-4">
      <div>
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
      {right}
    </div>
    <div className="space-y-4">{children}</div>
  </div>
);

const FormRow = ({ children, className = "" }) => (
  <div className={`grid grid-cols-1 gap-4 ${className}`}>{children}</div>
);

/* Corrigido: recebe basePrice e isRepresentative + inputName + onPriceEdit */
const QueryRow = ({
  idxCat,
  index,
  query,
  register,
  control,
  basePrice,
  isRepresentative,
  inputName,
  onPriceEdit,
}) => {
  const id = `q-${idxCat}-${index}`;

  return (
    <div className="rounded-xl border bg-white py-3 px-3 flex flex-col gap-2 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <label htmlFor={id} className="flex items-center cursor-pointer">
            <Controller
              name={`${inputName}.isActive`}
              control={control}
              render={({ field }) => (
                <Switch
                  {...field}
                  checked={!!field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  color="success"
                  size="medium"
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "var(--primary)",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "var(--primary)",
                    },
                  }}
                  id={id}
                />
              )}
            />
            <span className="text-sm font-medium text-gray-800">
              {query.name}
            </span>
          </label>

          {isRepresentative && (
            <span className="text-xs text-gray-500 ml-3">
              Preço de custo: {formatToReal(basePrice ?? query.price)}
            </span>
          )}
        </div>

        <div className="flex items-center gap-3">
          <CurrencyInput
            register={register}
            name={`${inputName}.price`}
            className="w-32"
            label="Preço"
            control={control}
            // marca como editado sempre que o usuário mexer
            onChange={() => onPriceEdit && onPriceEdit(`${inputName}.price`)}
          />
          <Input
            type="hidden"
            register={register}
            name={`${inputName}.QueryId`}
          />
        </div>
      </div>
    </div>
  );
};

const ReviewItem = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 py-2 border-b last:border-b-0">
    <span className="text-sm text-gray-500">{label}</span>
    <span className="text-sm font-medium text-gray-900 text-right break-all">
      {value || "—"}
    </span>
  </div>
);

const AdminRepresentativeForm = () => {
  const navigate = useNavigate();
  const { userPermission, user } = useApplication();
  const [activeStep, setActiveStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorQuery, setErrorQuery] = useState(false);

  // campos de preço que o usuário já editou (trava de sobrescrita)
  const userEditedPrice = useRef(new Set());
  const markUserEdited = (name) => userEditedPrice.current.add(name);
  const clearUserEdited = () => userEditedPrice.current.clear();

  // só aplica preço de tabela quando a tabela mudar
  const lastAppliedTableIdRef = useRef(null);

  const today = moment();
  const creationDay = today.clone().subtract(1, "day").date();
  const expirationDay = today.clone().subtract(1, "day").add(10, "days").date();
  const formatDay = (d) => d.toString().padStart(2, "0");

  const defaultValues = {
    company: {
      typeCustomer: "CPF",
      typePayment: "POS",
      generateAutomaticPayment: 1,
      paymentCreationDay: formatDay(creationDay),
      paymentExpirationDay: formatDay(expirationDay),
      priceSurvey: "10",
      priceCV: "10",
    },
    representative: {
      representativeUrl: "",
      accessToken: "",
    },
  };

  const {
    register,
    getValues,
    handleSubmit,
    setValue,
    setError,
    watch,
    control,
    formState: { errors },
    clearErrors,
    reset,
    getFieldState,
  } = useForm({ defaultValues });

  const { setCep, address, loading: loadingCEP } = useCEP();

  // Tabelas & categorias
  const { data: tables } = useQuery(["tables"], async () => {
    const { data } = await axios.get(`${ADMIN.GET_TABLES}?itemsPerPage=-1`);
    return data.data.data;
  });

  const { data: categories } = useQuery(["categories"], async () => {
    const { data } = await axios.get(`${CATEGORIES.GET_ALL}`);
    // Pré-popula ids/preços padrão (como string "1.23")
    data.data.data?.forEach((category, catIdx) => {
      category.Queries?.forEach((query, queryIdx) => {
        setValue(
          `categories[${catIdx}].queries[${queryIdx}].QueryId`,
          query.id
        );
        setValue(
          `categories[${catIdx}].queries[${queryIdx}].price`,
          toFixedStr(query.price)
        );
      });
    });
    return data.data.data;
  });

  const { data: tableQueries } = useQuery(
    ["tableQueries", watch("company.tableId")],
    async () => {
      if (watch("company.tableId")) {
        const { data: items } = await axios.get(
          `${ADMIN.GET_TABLE_QUERIES}/${watch("company.tableId")}`
        );
        return items.data;
      }
      return null;
    },
    { enabled: !!watch("company.tableId") }
  );

  // Herdar preços/flags da tabela selecionada
  useEffect(() => {
    const t = tables?.find((tb) => tb.id == watch("company.tableId"));
    setValue("company.vcm", t?.vcm || "");
    setValue("company.hasSurvey", t?.hasSurvey || "");
    setValue("company.hasCV", t?.hasCV || "");
    setValue("company.priceSurvey", toFixedStr(t?.priceSurvey ?? "10"));
    setValue("company.priceCV", toFixedStr(t?.priceCV ?? "10"));
  }, [watch("company.tableId"), tables, setValue]);

  // Aplicar preços da tabela nas queries, sem sobrescrever edição do usuário,
  // e apenas quando a tabela efetivamente muda
  useEffect(() => {
    const tableId = watch("company.tableId");
    if (!categories || !tableQueries || !tableId) return;

    // só aplica quando a tabela mudou (evita refetch sobrescrevendo)
    if (lastAppliedTableIdRef.current === tableId) return;

    categories.forEach((category, catIdx) => {
      category?.Queries?.forEach((query, index) => {
        const fieldBase = `categories[${catIdx}].queries[${index}]`;
        // garante o QueryId
        setValue(`${fieldBase}.QueryId`, query.id, {
          shouldDirty: false,
          shouldValidate: true,
        });

        const tq = tableQueries.find((cq) => cq.QueryId === query.id);
        if (!tq) return;

        // liga a query
        setValue(`${fieldBase}.isActive`, true, {
          shouldDirty: false,
          shouldValidate: true,
        });

        const priceField = `${fieldBase}.price`;
        const { isDirty } = getFieldState(priceField);

        // se o usuário mexeu, não sobrescreve
        if (userEditedPrice.current.has(priceField) || isDirty) return;

        // se o valor atual já é igual ao da tabela, não precisa setar
        const currentVal = getValues(priceField);
        const tableValStr = toFixedStr(tq.price);
        if (currentVal === tableValStr) return;

        // aplica preço da tabela SEM marcar dirty
        setValue(priceField, tableValStr, {
          shouldDirty: false,
          shouldValidate: true,
        });
      });
    });

    lastAppliedTableIdRef.current = tableId;
  }, [categories, tableQueries, setValue, getFieldState, getValues, watch]);

  // CEP autofill
  useEffect(() => {
    setValue("company.address", address?.logradouro);
    setValue("company.addressNeighborhood", address?.bairro);
    setValue("company.addressCity", address?.localidade);
  }, [address, setValue]);

  const onBlurSearchCEP = () => {
    setCep(getValues("company.cep"));
  };

  // Dedupe/validações
  const onBlurInfo = async (e, type) => {
    try {
      await axios.get(
        `${ADMIN.GET_COMPANY_BY_INFO}/${e.target.value}?type=${type}`
      );
      setError(`${e.target.name}`, {
        type: "manual",
        message: "Já possui este campo cadastrado!",
      });
    } catch {
      clearErrors(e.target.name);
    }
  };

  const onBlurEmailInfo = async (e) => {
    try {
      await axios.get(`${USERS.GET_USER_BY_EMAIL}/${e.target.value}`);
      clearErrors(e.target.name);
    } catch {
      setError(`${e.target.name}`, {
        type: "manual",
        message: "Já possui este campo cadastrado!",
      });
    }
  };

  // Validação CPF/CNPJ
  const checkDocument = (e, type) => {
    const unmasked = e.target.value.replace(/\D/g, "");
    const isValid =
      unmasked.length === 11
        ? isValidCPF(unmasked)
        : unmasked.length === 14
        ? isValidCNPJ(unmasked)
        : false;
    if (!isValid) {
      setError(`company.${type}`, {
        type: "manual",
        message: "Documento inválido",
      });
    } else {
      clearErrors(`company.${type}`);
      onBlurInfo(e, type);
    }
  };

  // Ações de etapas
  const handleStep1Submit = () => setActiveStep(2);
  const handleStep2Submit = () => setActiveStep(3);

  const handleSelectAll = () => {
    setValue("company.hasSurvey", true);
    setValue("company.hasCV", true);
    categories?.forEach((category, catIndex) =>
      category.Queries?.forEach((_, index) =>
        setValue(`categories[${catIndex}].queries[${index}].isActive`, true, {
          shouldDirty: false,
          shouldValidate: true,
        })
      )
    );
  };

  const handleQueriesData = () => {
    handleSubmit((data) => {
      let queries = [];
      data.categories?.forEach((cat) => {
        cat.queries?.forEach((q) => {
          if (q.isActive) {
            queries.push({
              QueryId: q.QueryId,
              price: q.price === "" || q.price == null ? null : Number(q.price),
              isActive: true,
            });
          }
        });
      });
      window.form = { ...window.form, queries };
      setActiveStep(4);
    })();
  };

  const handleFinishSubmit = async (data) => {
    setIsSubmitting(true);
    setErrorQuery(false);
    try {
      // flatten queries e converter price para número
      let newArrayQueries = [];
      data.categories?.forEach((cat) =>
        cat.queries?.forEach((q) => newArrayQueries.push(q))
      );

      data.queries = newArrayQueries
        .filter((q) => q.isActive)
        .map((q) => ({
          QueryId: q.QueryId,
          price: q.price === "" || q.price == null ? null : Number(q.price),
          isActive: true,
        }));

      await axios.post(ADMIN.NEW_REPRESENTATIVE, data);
      setActiveStep(5);
    } catch (error) {
      setErrorQuery(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const userEdited = useRef({
    address: false,
    addressNeighborhood: false,
    addressCity: false,
  });

  // marcar como editado via register.onChange (endereços)
  const regAddress = register("company.address", {
    onChange: () => (userEdited.current.address = true),
  });
  const regNeighborhood = register("company.addressNeighborhood", {
    onChange: () => (userEdited.current.addressNeighborhood = true),
  });
  const regCity = register("company.addressCity", {
    onChange: () => (userEdited.current.addressCity = true),
  });

  useEffect(() => {
    if (!address) return;
    const { logradouro, bairro, localidade } = address || {};

    if (logradouro && !userEdited.current.address) {
      setValue("company.address", logradouro, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
    if (bairro && !userEdited.current.addressNeighborhood) {
      setValue("company.addressNeighborhood", bairro, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
    if (localidade && !userEdited.current.addressCity) {
      setValue("company.addressCity", localidade, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
  }, [address, setValue]);

  // Derivados de UI
  const isCPF = watch("company.typeCustomer") === "CPF";
  const powerUser = userPermission === "POWER_USER";

  return (
    <div className="bg-white rounded-xl p-2 md:p-5 space-y-6">
      <StepHeader activeStep={activeStep} />

      {/* STEP 1 */}
      {activeStep === 1 && (
        <form onSubmit={handleSubmit(handleStep1Submit)} className="space-y-6">
          <SectionCard
            title="Dados do revendedor"
            subtitle="Preencha as informações cadastrais e de contato."
            right={
              loadingCEP ? (
                <span className="inline-flex items-center gap-2 text-xs text-gray-500">
                  <Spinner size="xs" /> Buscando endereço pelo CEP…
                </span>
              ) : null
            }
          >
            <FormRow className="lg:grid-cols-3">
              <SelectInput
                register={register}
                label="Tipo de revendedor"
                name="company.typeCustomer"
              >
                <option value="CPF">Pessoa Física</option>
                <option value="CNPJ">Pessoa Jurídica</option>
              </SelectInput>

              {isCPF ? (
                <>
                  <div>
                    <Input
                      type="text"
                      register={register}
                      name="company.name"
                      label="Nome completo"
                      required
                    />
                  </div>
                  <div>
                    <Controller
                      name="company.document"
                      control={control}
                      defaultValue=""
                      rules={{ required: "Este campo é obrigatório" }}
                      render={({ field, fieldState }) => (
                        <>
                          <CPFInput
                            name="company.document"
                            label="CPF"
                            required
                            {...field}
                            onBlur={mergeHandlers(field.onBlur, (e) =>
                              checkDocument(e, "document")
                            )}
                            error={fieldState.error?.message}
                          />
                        </>
                      )}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Input
                      type="text"
                      register={register}
                      name="company.socialReason"
                      label="Razão Social"
                      required
                    />
                  </div>
                  <div>
                    <Controller
                      name="company.document"
                      control={control}
                      defaultValue=""
                      rules={{ required: "Este campo é obrigatório" }}
                      render={({ field, fieldState }) => (
                        <>
                          <CNPJInput
                            name="company.document"
                            label="CNPJ"
                            required
                            {...field}
                            onBlur={mergeHandlers(field.onBlur, (e) =>
                              checkDocument(e, "document")
                            )}
                            error={fieldState.error?.message}
                          />
                        </>
                      )}
                    />
                  </div>
                </>
              )}
            </FormRow>

            {!isCPF && (
              <FormRow className="lg:grid-cols-3">
                <div>
                  <Input
                    type="text"
                    register={register}
                    name="company.responsibleName"
                    label="Nome do responsável"
                    required
                  />
                </div>
                <div>
                  <Controller
                    name="company.responsibleCPF"
                    control={control}
                    defaultValue=""
                    rules={{ required: "Este campo é obrigatório" }}
                    render={({ field, fieldState }) => (
                      <>
                        <CPFInput
                          name="company.responsibleCPF"
                          label="CPF do responsável"
                          required
                          {...field}
                          onBlur={mergeHandlers(field.onBlur, (e) =>
                            checkDocument(e, "responsibleCPF")
                          )}
                          error={fieldState.error?.message}
                        />
                      </>
                    )}
                  />
                </div>
                <SelectInput
                  register={register}
                  label="Cargo"
                  name="company.position"
                >
                  <option value="SOCIO">Sócio/Administrador</option>
                  <option value="GERENTE">Gerente</option>
                  <option value="ADM">Administrador</option>
                </SelectInput>
              </FormRow>
            )}

            <FormRow className="lg:grid-cols-3">
              <div>
                <Input
                  type="email"
                  register={register}
                  name="company.email"
                  label="E-mail"
                  required
                  onBlur={(e) => onBlurInfo(e, "email")}
                />
                {errors.company?.email && (
                  <small className="text-xs text-red-500">
                    {errors.company.email.message}
                  </small>
                )}
              </div>

              <div>
                <Controller
                  name="company.phoneNumber"
                  control={control}
                  defaultValue=""
                  rules={{ required: "Este campo é obrigatório" }}
                  render={({ field, fieldState }) => (
                    <>
                      <PhoneInput
                        name="company.phoneNumber"
                        label="Celular"
                        required
                        {...field}
                        error={fieldState.error?.message}
                      />
                    </>
                  )}
                />
              </div>

              <SelectInput
                register={register}
                label="Tipo de pagamento"
                name="company.typePayment"
                disabled
              >
                <option value="POS">Pós-pago</option>
              </SelectInput>

              {!isCPF && (
                <div>
                  <Input
                    type="email"
                    register={register}
                    name="company.financeEmail"
                    label="E-mail do financeiro"
                    required
                    autoComplete="off"
                    onBlur={(e) => onBlurInfo(e, "financeEmail")}
                  />
                  {errors.company?.financeEmail && (
                    <small className="text-xs text-red-500">
                      {errors.company.financeEmail.message}
                    </small>
                  )}
                  <div className="text-xs text-gray-500 italic mt-1">
                    Envio da cobrança para este e-mail, caso haja.
                  </div>
                </div>
              )}
            </FormRow>

            <div>
              <div className="font-semibold pt-3">Endereço do revendedor</div>
              <div className="text-gray-500 text-sm mt-1">
                Preencha o endereço do revendedor
              </div>
            </div>

            <FormRow className="lg:grid-cols-3">
              <Input
                register={register}
                name="company.cep"
                label="CEP"
                required
                onBlur={onBlurSearchCEP}
              />
              <Input
                register={register}
                name="company.address"
                onChange={() => (userEdited.current.address = true)}
                label="Endereço"
                required
              />
              <Input
                type="number"
                register={register}
                name="company.addressNumber"
                label="Número"
                required
              />
            </FormRow>

            <FormRow className="lg:grid-cols-3">
              <Input
                register={register}
                name="company.addressComplement"
                label="Complemento"
              />
              <Input
                register={register}
                onChange={() => (userEdited.current.addressNeighborhood = true)}
                name="company.addressNeighborhood"
                label="Bairro"
                required
              />
              <Input
                register={register}
                onChange={() => (userEdited.current.addressCity = true)}
                name="company.addressCity"
                label="Cidade"
                required
              />
            </FormRow>
          </SectionCard>

          {powerUser && (
            <SectionCard
              title="White-label (opcional)"
              subtitle="Vincule uma URL e um token do Asaas para este revendedor."
            >
              <FormRow className="lg:grid-cols-2">
                <Input
                  register={register}
                  name="representative.representativeUrl"
                  label="URL da White-label"
                />
                <Input
                  register={register}
                  name="representative.accessToken"
                  label="Token Asaas"
                />
                <div className="flex items-end"></div>
              </FormRow>
            </SectionCard>
          )}
          <div className="flex justify-center">
            <Button type="submit" className="shadow md:max-w-60 px-10 ">
              Próximo
            </Button>
          </div>
        </form>
      )}

      {/* STEP 2 */}
      {activeStep === 2 && (
        <form onSubmit={handleSubmit(handleStep2Submit)} className="space-y-6">
          <SectionCard
            title="Usuário administrador"
            subtitle="Crie o primeiro usuário para acessar o painel."
          >
            <div className="flex justify-end gap-2 items-center  mb-4">
              <Button
                size="xs"
                type="button"
                className="w-fit self-end"
                onClick={() => {
                  const email = watch("company.email");
                  const name =
                    watch("company.responsibleName") || watch("company.name");
                  const phone = watch("company.phoneNumber");

                  if (email) setValue("customer.email", email);
                  if (name) setValue("customer.name", name);
                  if (phone) setValue("customer.phoneNumber", phone);
                }}
              >
                Reutilizar dados preenchidos do representante
              </Button>
              <div>
                <Tooltip
                  id="tooltip-link"
                  content={
                    <div className="max-w-md relative">
                      Esse botão preenche automaticamente os campos abaixo com o
                      e-mail, nome e celular informados na etapa anterior.
                    </div>
                  }
                />
                <FaRegQuestionCircle
                  data-tooltip-id="tooltip-link"
                  className="text-gray-500"
                />
              </div>
            </div>
            {/* linha 1: nome + e-mail */}
            <FormRow className="lg:grid-cols-2">
              <Input
                register={register}
                name="customer.name"
                label="Nome"
                required
              />
              <div>
                <Input
                  type="email"
                  register={register}
                  name="customer.email"
                  label="E-mail"
                  required
                  onBlur={onBlurEmailInfo}
                />
                {errors.customer?.email && (
                  <small className="text-xs text-red-500">
                    {errors.customer.email.message}
                  </small>
                )}
              </div>
            </FormRow>

            {/* linha 2: celular + senha */}
            <FormRow className="md:grid-cols-2">
              <div>
                <Controller
                  name="customer.phoneNumber"
                  control={control}
                  defaultValue=""
                  rules={{ required: "Este campo é obrigatório" }}
                  render={({ field, fieldState }) => (
                    <>
                      <PhoneInput
                        name="customer.phoneNumber"
                        label="Celular"
                        required
                        {...field}
                        error={fieldState.error?.message}
                      />
                    </>
                  )}
                />
              </div>
              <div>
                <Input
                  type="password"
                  register={register}
                  name="customer.password"
                  label="Senha"
                  autoComplete="off"
                />
                <small className="text-xs text-gray-500">
                  Se vazio, usaremos a senha padrão: 123456
                </small>
              </div>
            </FormRow>
          </SectionCard>
          <div className="flex justify-center gap-2">
            <Button
              onClick={() => setActiveStep(1)}
              color="gray"
              className="md:max-w-60"
            >
              Voltar
            </Button>
            <Button type="submit" className="md:max-w-60">
              Próximo
            </Button>
          </div>
        </form>
      )}

      {/* STEP 3 */}
      {activeStep === 3 && (
        <form className="space-y-6">
          <SectionCard
            title="Consultas liberadas"
            subtitle="Selecione as consultas e (opcionalmente) aplique uma tabela pré-definida."
            right={
              <Button size="xs" onClick={() => handleSelectAll()}>
                Selecionar todos
              </Button>
            }
          >
            {/* seletor de tabela */}
            <div className="w-full md:w-96">
              <SelectInput
                register={register}
                label="Tabela pré-definida"
                name="company.tableId"
              >
                <option value="">Nenhuma selecionada</option>
                {tables?.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} — {t.vcm ? formatToReal(t.vcm) : "Sem VCM"}
                  </option>
                ))}
              </SelectInput>
              {!!watch("company.tableId") && (
                <div className="text-xs text-gray-500 mt-1">
                  VCM:{" "}
                  {watch("company.vcm")
                    ? formatToReal(watch("company.vcm"))
                    : "—"}{" "}
                  • Vistoria: {watch("company.hasSurvey") ? "Sim" : "Não"} • CV:{" "}
                  {watch("company.hasCV") ? "Sim" : "Não"}
                </div>
              )}
            </div>

            {/* categorias */}
            <Accordion>
              {categories?.map((category, idxCat) => (
                <Accordion.Panel key={idxCat}>
                  <Accordion.Title>{category.name}</Accordion.Title>
                  <Accordion.Content>
                    {!category?.Queries?.length ? (
                      <div className="text-sm text-center text-gray-500">
                        Não há consultas nesta categoria.
                      </div>
                    ) : (
                      <div className="space-y-6">
                        {/* ======== PRINCIPAIS ======== */}
                        <div>
                          <div className="text-xs font-semibold text-gray-600 mb-2">
                            Consultas principais
                          </div>
                          {category.Queries.some((q) => !q.isAdditional) ? (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                              {category.Queries.map((query, index) => {
                                if (query.isAdditional) return null;

                                const basePrice = getBasePrice
                                  ? getBasePrice(query, tableQueries)
                                  : tableQueries?.find(
                                      (tq) => tq.QueryId === query.id
                                    )?.price ?? query.price;

                                const isRepresentative = isRep
                                  ? isRep(user)
                                  : String(user?.Company?.companyTypeId || "")
                                      .toUpperCase()
                                      .includes("REPRESENT");

                                const inputName = `categories[${idxCat}].queries[${index}]`;

                                return (
                                  <QueryRow
                                    key={`main-${idxCat}-${index}`}
                                    idxCat={idxCat}
                                    index={index}
                                    query={query}
                                    register={register}
                                    control={control}
                                    basePrice={basePrice}
                                    isRepresentative={isRepresentative}
                                    inputName={inputName}
                                    onPriceEdit={markUserEdited}
                                  />
                                );
                              })}
                            </div>
                          ) : (
                            <div className="text-sm text-gray-500">
                              Esta categoria não possui consultas principais.
                            </div>
                          )}
                        </div>

                        {/* ======== ADICIONAIS ======== */}
                        <div>
                          <div className="text-xs font-semibold text-gray-600 mb-2">
                            Consultas adicionais
                          </div>
                          {category.Queries.some((q) => !!q.isAdditional) ? (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                              {category.Queries.map((query, index) => {
                                if (!query.isAdditional) return null;

                                const basePrice = getBasePrice
                                  ? getBasePrice(query, tableQueries)
                                  : tableQueries?.find(
                                      (tq) => tq.QueryId === query.id
                                    )?.price ?? query.price;

                                const isRepresentative = isRep
                                  ? isRep(user)
                                  : String(user?.Company?.companyTypeId || "")
                                      .toUpperCase()
                                      .includes("REPRESENT");

                                const inputName = `categories[${idxCat}].queries[${index}]`;

                                return (
                                  <QueryRow
                                    key={`add-${idxCat}-${index}`}
                                    idxCat={idxCat}
                                    index={index}
                                    query={query}
                                    register={register}
                                    control={control}
                                    basePrice={basePrice}
                                    isRepresentative={isRepresentative}
                                    inputName={inputName}
                                    onPriceEdit={markUserEdited}
                                  />
                                );
                              })}
                            </div>
                          ) : (
                            <div className="text-sm text-gray-500">
                              Esta categoria não possui consultas adicionais.
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </Accordion.Content>
                </Accordion.Panel>
              ))}
            </Accordion>
          </SectionCard>
          <div className="flex justify-center gap-2">
            <Button
              onClick={() => setActiveStep(2)}
              color="gray"
              className="md:max-w-60"
            >
              Voltar
            </Button>
            <Button onClick={handleQueriesData} className="md:max-w-60">
              Próximo
            </Button>
          </div>
        </form>
      )}

      {/* STEP 4 */}
      {activeStep === 4 && (
        <form onSubmit={handleSubmit(handleFinishSubmit)} className="space-y-6">
          <SectionCard title="Revisão — revendedor">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {isCPF ? (
                <ReviewItem label="Nome" value={watch("company.name")} />
              ) : (
                <ReviewItem
                  label="Razão social"
                  value={watch("company.socialReason")}
                />
              )}
              <ReviewItem label="E-mail" value={watch("company.email")} />
              <ReviewItem
                label="Celular"
                value={watch("company.phoneNumber")}
              />
              {!isCPF && (
                <>
                  <ReviewItem
                    label="Responsável"
                    value={watch("company.responsibleName")}
                  />
                  <ReviewItem
                    label="CPF do responsável"
                    value={watch("company.responsibleCPF")}
                  />
                </>
              )}
              <ReviewItem label="CEP" value={watch("company.cep")} />
              <ReviewItem label="Endereço" value={watch("company.address")} />
              <ReviewItem
                label="Número"
                value={watch("company.addressNumber")}
              />
              <ReviewItem
                label="Complemento"
                value={watch("company.addressComplement")}
              />
              <ReviewItem
                label="Bairro"
                value={watch("company.addressNeighborhood")}
              />
              <ReviewItem label="Cidade" value={watch("company.addressCity")} />
              {powerUser && (
                <>
                  <ReviewItem
                    label="URL White-label"
                    value={watch("representative.representativeUrl")}
                  />
                  <ReviewItem
                    label="Token Asaas"
                    value={watch("representative.accessToken")}
                  />
                </>
              )}
            </div>
          </SectionCard>

          <SectionCard title="Revisão — usuário">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ReviewItem label="Nome" value={watch("customer.name")} />
              <ReviewItem label="E-mail" value={watch("customer.email")} />
              <ReviewItem
                label="Celular"
                value={watch("customer.phoneNumber")}
              />
            </div>
          </SectionCard>
          <div className="flex justify-center gap-2">
            <Button
              onClick={() => setActiveStep(2)}
              color="gray"
              className="md:max-w-60"
            >
              Voltar
            </Button>
            <Button
              type="submit"
              className="md:max-w-60"
              isProcessing={isSubmitting}
            >
              Finalizar
            </Button>
          </div>

          {errorQuery && (
            <Alert color="failure" className="mt-3">
              Erro ao tentar cadastrar o revendedor!
            </Alert>
          )}
        </form>
      )}

      {/* STEP 5 */}
      {activeStep === 5 && (
        <div className="relative overflow-hidden rounded-2xl border bg-white shadow-md">
          {/* fundo com gradiente suave */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-emerald-50 via-white to-emerald-100 opacity-70" />

          {/* confettis decorativos */}
          <svg
            className="pointer-events-none absolute -top-6 -left-6 w-28 h-28 opacity-50"
            viewBox="0 0 100 100"
            fill="none"
          >
            <circle cx="20" cy="20" r="3" fill="#10B981" />
            <rect x="60" y="10" width="6" height="6" rx="1" fill="#34D399" />
            <circle cx="85" cy="30" r="2.5" fill="#A7F3D0" />
            <rect x="12" y="70" width="5" height="5" rx="1" fill="#6EE7B7" />
            <circle cx="42" cy="85" r="2" fill="#10B981" />
          </svg>
          <svg
            className="pointer-events-none absolute -bottom-6 -right-6 w-28 h-28 opacity-50"
            viewBox="0 0 100 100"
            fill="none"
          >
            <circle cx="80" cy="80" r="3" fill="#10B981" />
            <rect x="30" y="84" width="6" height="6" rx="1" fill="#34D399" />
            <circle cx="18" cy="62" r="2.5" fill="#A7F3D0" />
            <rect x="82" y="22" width="5" height="5" rx="1" fill="#6EE7B7" />
            <circle cx="58" cy="12" r="2" fill="#10B981" />
          </svg>

          <div className="relative p-8 flex flex-col items-center text-center">
            {/* ícone grande em destaque */}
            <div className="mb-4 inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-100">
              <FaCheckCircle className="w-12 h-12 text-emerald-600" />
            </div>

            <h3 className="text-2xl font-bold text-gray-900">
              Representante cadastrado com sucesso!
            </h3>
            <p className="text-sm text-gray-600 mt-2 max-w-xl">
              Tudo certo por aqui. Você já pode gerenciar permissões, convidar
              usuários ou cadastrar outro representante.
            </p>

            {/* resumo rápido */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-2xl text-left">
              <div className="rounded-xl border bg-white/70 p-4">
                <div className="text-xs uppercase tracking-wide text-gray-500">
                  Representante
                </div>
                <div className="font-medium text-gray-900">
                  {watch("company.typeCustomer") === "CPF"
                    ? watch("company.name")
                    : watch("company.socialReason")}
                </div>
              </div>
              <div className="rounded-xl border bg-white/70 p-4">
                <div className="text-xs uppercase tracking-wide text-gray-500">
                  E-mail
                </div>
                <div className="font-medium text-gray-900 break-words">
                  {watch("company.email")}
                </div>
              </div>
              <div className="rounded-xl border bg-white/70 p-4">
                <div className="text-xs uppercase tracking-wide text-gray-500">
                  Celular
                </div>
                <div className="font-medium text-gray-900">
                  {watch("company.phoneNumber")}
                </div>
              </div>
              <div className="rounded-xl border bg-white/70 p-4">
                <div className="text-xs uppercase tracking-wide text-gray-500">
                  Tipo de pagamento
                </div>
                <div className="font-medium text-gray-900">
                  {watch("company.typePayment")}
                </div>
              </div>
            </div>

            {/* ações */}
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button
                className="md:max-w-60"
                onClick={() => {
                  reset(undefined, { keepValues: false });
                  // limpa as travas para novo cadastro
                  clearUserEdited();
                  lastAppliedTableIdRef.current = null;
                  setActiveStep(1);
                }}
              >
                Cadastrar outro
              </Button>
              <Button
                color="light"
                className="md:max-w-60"
                onClick={() => {
                  navigate("/admin/representatives");
                }}
              >
                Ir para a lista
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRepresentativeForm;
